import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CustomCourseRequestPr from "../components/CustomCourseRequestPr";
import { MonoButton } from "../kits";
import { usePlaceholder } from "../context/usePlaceholder";

function SearchOverlay({
  searchQuery,
  filteredCourses = [],
  filteredBlog = [],
  initialView = "search",
  onSearchChange,
  onClose,
  onResultClick,
}) {
  const [view, setView] = useState(initialView);
  const { nameSinglePlaceholder } = usePlaceholder();

  const hasQuery = searchQuery.trim().length > 0;
  const totalHits = filteredCourses.length + filteredBlog.length;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="fixed inset-0 z-50 bg-[#0b0b0b]/88 backdrop-blur-sm overflow-y-auto overscroll-contain"
    >
      <div className="w-full min-h-[100dvh] flex flex-col items-center pt-20 pb-10 px-4 font-mono">
        {view === "search" ? (
          <div className="w-full max-w-2xl rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-xl shadow-black/60">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
              <button
                type="button"
                onClick={onClose}
                aria-label="Suche schliessen"
                className="w-3 h-3 rounded-full bg-[#ff5f57] hover:brightness-110"
              />
              <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
              <span className="w-3 h-3 rounded-full bg-[#28c840]" />
              <span className="ml-3 text-xs text-[#AC8E66] select-none">search</span>
              <span className="ml-auto text-[11px] text-[#d0cbb8]/35">esc</span>
            </div>

            <div className="px-4 sm:px-6 py-5 sm:py-6 bg-[#0F0F0F] text-[#d0cbb8]">
              <div className="flex items-center gap-2">
                <span className="text-[#AC8E66] text-sm sm:text-base shrink-0">$</span>
                <span className="text-[#d0cbb8]/60 text-sm sm:text-base shrink-0">search</span>
                <input
                  type="text"
                  placeholder='"react", "beratung", "c4d" ...'
                  value={searchQuery}
                  onChange={onSearchChange}
                  className="flex-1 bg-transparent text-sm sm:text-base text-[#d0cbb8] placeholder-[#d0cbb8]/25 outline-none border-none ring-0 focus:outline-none focus:ring-0 focus:border-none caret-[#AC8E66] font-mono"
                  autoFocus
                />
              </div>

              {!hasQuery && (
                <p className="mt-3 text-xs text-[#d0cbb8]/50">
                  <span className="text-[#AC8E66]/70">&gt;</span> tippe einen begriff und druecke enter
                </p>
              )}

              {hasQuery && totalHits > 0 && (
                <p className="mt-3 text-sm text-green-400">
                  <span className="text-green-400/70">&gt;</span> found {totalHits} result{totalHits === 1 ? "" : "s"}...
                </p>
              )}

              {hasQuery && (
                <div className="mt-4 space-y-2">
                  {filteredCourses.map((course) => (
                    <Link
                      key={`course-${course.id}`}
                      to={`/course/${course.slug}`}
                      className="block rounded px-2 py-2 text-sm hover:bg-[#AC8E66]/10 transition-colors"
                      onClick={() => {
                        onResultClick();
                        onClose();
                      }}
                    >
                      <span className="text-[#AC8E66]/80">&gt;</span>{" "}
                      <span className="text-[#d0cbb8]">course:</span>{" "}
                      <span className="text-[#d0cbb8]/80">{course.title}</span>
                    </Link>
                  ))}

                  {filteredBlog.map((post) => (
                    <Link
                      key={`blog-${post.id ?? post.slug}`}
                      to={`/post/${post.slug}`}
                      className="block rounded px-2 py-2 text-sm hover:bg-[#AC8E66]/10 transition-colors"
                      onClick={() => {
                        onResultClick();
                        onClose();
                      }}
                    >
                      <span className="text-[#AC8E66]/80">&gt;</span>{" "}
                      <span className="text-[#d0cbb8]">post:</span>{" "}
                      <span className="text-[#d0cbb8]/80">{post.title}</span>
                    </Link>
                  ))}
                </div>
              )}

              {hasQuery && totalHits === 0 && (
                <div className="mt-4">
                  <p className="text-sm text-orange-400">
                    <span className="text-orange-400/70">&gt;</span> no matches for "{searchQuery}"
                  </p>
                  <p className="text-[#d0cbb8]/65 text-xs sm:text-sm mt-4 leading-relaxed">
                    Du stehst mitten im Projekt {nameSinglePlaceholder}? Dann sag mir einfach, was dir fehlt.
                  </p>
                  <MonoButton onClick={() => setView("request")} className="text-xs mt-4">
                    Anfrage stellen
                  </MonoButton>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="w-full max-w-2xl">
            <CustomCourseRequestPr
              initialStep="kategorie"
              onClose={() => setView("search")}
            />
            <button
              type="button"
              onClick={() => setView("search")}
              className="mt-4 text-xs text-[#d0cbb8]/50 hover:text-[#d0cbb8]"
            >
              zurueck zur suche
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="mt-4 text-xs text-[#d0cbb8]/50 hover:text-[#d0cbb8]"
        >
          close search
        </button>
      </div>
    </motion.div>
  );
}

export default SearchOverlay;
